import React from 'react';

import classNames from 'classnames';
import moment from 'moment-timezone';

import RecruitmentStatusSelect from './RecruitmentStatusSelect';
import { COLUMN_BUDGET_CODE, COLUMN_EMPLOYEE_NUMBER, COLUMN_END_DATE, COLUMN_UNIQUE_ID, COLUMN_VENUE_PERCENT } from './usePositionsColumns';

import { ZERO_DASH, formatFigure } from 'Helpers/formatService';

import CheckboxInput from 'CommonComponents/CheckboxInput';

import CheckIcon from 'Assets/icons/check.svg';
import EditIcon from 'Assets/icons/edit.svg';
import EyeIcon from 'Assets/icons/eye.svg';
import TrashIcon from 'Assets/icons/trash.svg';

const formatPercent = (value) => formatFigure(value, String, { precision: 2, suffix: '%' });

// Dates are serialised at UTC midnight; reading them in local time would shift a day west of UTC.
export const formatListDate = (value) => (value ? moment.utc(value).format('DD MMM YYYY') : '');

export const positionTotal = (position) => (position.cashflow ? Number(JSON.parse(position.cashflow).total) || 0 : 0);

export const departmentPath = (position) => [position.department?.name, position.costcenter?.name].filter(Boolean).join(' / ');

/** "total · %", "total" with the Venue % column off, or a dash with no allocation. */
export const VenueAllocation = ({ allocation, showPercent }) => {
    if (!allocation || !Number(allocation.total)) {
        return ZERO_DASH;
    }

    return showPercent ? `${formatFigure(allocation.total)} · ${formatPercent(allocation.percentage)}` : formatFigure(allocation.total);
};

export const PayRate = ({ position }) => (
    <>
        {position.payrate?.name || ''}
        {position.is_pay_rate_overridden && (
            <span
                className="tw-ms-1 tw-inline-flex tw-items-center tw-rounded tw-bg-amber-100 tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-amber-800"
                title={$t('Pay rate overridden')}
            >
                {$t('Override')}
            </span>
        )}
    </>
);

const PositionsTableRow = ({
    position,
    types,
    columns,
    showVenues,
    venues,
    allVenues,
    showUrl,
    editUrl,
    selectable,
    selected,
    statuses,
    statusEditable,
    statusSaving,
    showRecruitmentStatus,
    indented = false,
    onSelect,
    onStatusChange,
    onDelete,
}) => {
    const readOnly = !!window.budgetReadOnly;
    const subtitle = [departmentPath(position), position.reference_number].filter(Boolean).join(' · ');

    return (
        <tr className={classNames('tw-relative', { 'tw-bg-charcoal-10': indented })} data-position-row={position.id}>
            <td>
                {selectable && (
                    <CheckboxInput
                        aria-label={$t('Select {{name}}', { name: position.name })}
                        checked={selected}
                        onChange={(e) => onSelect(e.target.checked)}
                    />
                )}
            </td>
            <td className={classNames('!tw-whitespace-normal', { 'tw-ps-8': indented })}>
                {position.can.view ? (
                    <a className="tw-font-semibold" href={showUrl.replace('POSITION_ID', position.id)}>
                        {position.name}
                    </a>
                ) : (
                    <span className="tw-font-semibold">{position.name}</span>
                )}
                {!!subtitle && <div className="tw-text-2xs tw-text-charcoal-50">{subtitle}</div>}
            </td>
            <td>{types[position.type]}</td>
            <td>
                <RecruitmentStatusSelect
                    status={showRecruitmentStatus ? position.recruitment_status : null}
                    statuses={statuses}
                    editable={statusEditable && position.can.update}
                    disabled={statusSaving}
                    onChange={(statusId) => onStatusChange([position.id], statusId)}
                />
            </td>
            <td className="inbud-date-cell">{formatListDate(position.start_date)}</td>
            {columns[COLUMN_END_DATE] && <td className="inbud-date-cell">{formatListDate(position.end_date)}</td>}
            <td>
                <PayRate position={position} />
            </td>
            <td className="inbud-numeric-cell">{formatFigure(positionTotal(position))}</td>
            <td>{position.is_committed ? <CheckIcon className="tw-w-3 tw-h-3 tw-text-dark-green" aria-label={$t('Committed')} /> : ZERO_DASH}</td>
            <td>{position.supervisor?.unique_id}</td>
            {columns[COLUMN_UNIQUE_ID] && <td>{position.unique_id}</td>}
            {columns[COLUMN_BUDGET_CODE] && <td>{position.reference_number}</td>}
            {columns[COLUMN_EMPLOYEE_NUMBER] && (
                <td>
                    {position.budgetted_staff && (
                        <a href={showUrl.replace('POSITION_ID', position.budgetted_staff.id)}>{position.budgetted_staff.contract_reference}</a>
                    )}
                </td>
            )}
            <td className="inbud-numeric-cell">
                <VenueAllocation allocation={position.allocation_totals} showPercent={columns[COLUMN_VENUE_PERCENT]} />
            </td>
            {showVenues &&
                !!venues?.length &&
                (allVenues ?? position.all_venues)?.map((venue) => (
                    <td key={venue.id} className="inbud-numeric-cell !tw-bg-white">
                        <VenueAllocation allocation={venue.position_allocation} showPercent={columns[COLUMN_VENUE_PERCENT]} />
                    </td>
                ))}
            <td>
                {/* In-version read-only context: no view/edit/delete affordances. Gated on
                    context, not position.can.* — super-admins bypass policy via Gate::before
                    so those flags stay true. */}
                <div className="tw-flex tw-items-center tw-gap-x-3">
                    {!readOnly && position.can.view && (
                        <a title={$t('View position')} href={showUrl.replace('POSITION_ID', position.id)} className="!tw-text-charcoal-75">
                            <EyeIcon className="tw-w-3.5 tw-h-3.5" />
                        </a>
                    )}
                    {!readOnly && position.can.update && (
                        <a
                            title={$t('Edit position')}
                            href={editUrl.replace('POSITION_ID', position.id)}
                            className="!tw-text-charcoal-75 edit-locked-budget-entity"
                        >
                            <EditIcon className="tw-w-3.5 tw-h-3.5" />
                        </a>
                    )}
                    {!readOnly && position.can.delete && (
                        <button
                            type="button"
                            onClick={() => onDelete(position)}
                            className="edit-locked-budget-entity tw-text-red"
                            title={$t('Delete position')}
                        >
                            <TrashIcon className="tw-w-3.5 tw-h-3.5" />
                        </button>
                    )}
                </div>
            </td>
        </tr>
    );
};

export default PositionsTableRow;
